/* Tailigent - bundle offer widget.
   Three purchase options (single ball / 1 ball + shell / 2 balls + shell).
   The FIRST ball of every option comes from the theme's main product variant
   picker (read live). The widget only picks the extras: shell (A), or second
   ball + shell (B). One option is always selected.

   Adds the items to the cart in one request, reusing the theme's cart drawer /
   notification. "Buy now" adds then redirects to checkout.

   Broadcasts the current selection on the document as `tailigent:bundle-change`
   (detail: { mode, price, items }) so the sticky footer can mirror it, and
   answers `tailigent:bundle-request` with the current state. */

if (!customElements.get('tailigent-bundle')) {
  customElements.define(
    'tailigent-bundle',
    class TailigentBundle extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.productInfo = this.closest('.product__info-container');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.modeInputs = Array.from(this.querySelectorAll('.tailigent-bundle__options input[type="radio"]'));
        this.panels = Array.from(this.querySelectorAll('.tailigent-bundle__panel'));
        this.singleCard = this.querySelector('.tailigent-bundle__card--single');
        this.errorEl = this.querySelector('.tailigent-bundle__error');
        this.loading = false;
        this.redirecting = false;

        this.modeInputs.forEach((radio) => radio.addEventListener('change', this.onModeChange.bind(this)));

        this.querySelectorAll('.tailigent-bundle__atc').forEach((btn) =>
          btn.addEventListener('click', (e) => this.addBundle(false, e.currentTarget.closest('.tailigent-bundle__panel')))
        );
        this.querySelectorAll('.tailigent-bundle__buy-now').forEach((btn) =>
          btn.addEventListener('click', (e) => this.addBundle(true, e.currentTarget.closest('.tailigent-bundle__panel')))
        );

        // Re-broadcast when widget colors change.
        this.addEventListener('change', (e) => {
          if (e.target.classList.contains('tailigent-bundle__variant-input')) this.broadcast();
        });

        // The first ball follows the main product variant picker; re-broadcast
        // when its selection changes (the theme dispatches a bubbling change).
        this.mainBallInput = this.getMainBallInput();
        if (this.mainBallInput) this.mainBallInput.addEventListener('change', () => this.broadcast());

        // Answer state requests (e.g. from the sticky footer).
        this._onRequest = () => this.broadcast();
        document.addEventListener('tailigent:bundle-request', this._onRequest);

        this.onModeChange();
      }

      disconnectedCallback() {
        document.removeEventListener('tailigent:bundle-request', this._onRequest);
      }

      getMainBallInput() {
        const form = document.getElementById(`product-form-${this.sectionId}`);
        return form ? form.querySelector('input[name="id"]') : null;
      }

      getMainBallId() {
        const input = this.mainBallInput || this.getMainBallInput();
        const id = input && input.value ? input.value : this.dataset.defaultVariantId;
        return id || null;
      }

      get activeInput() {
        return this.modeInputs.find((r) => r.checked) || null;
      }

      get activeMode() {
        return this.activeInput ? this.activeInput.value : null;
      }

      activePanel() {
        const mode = this.activeMode;
        return this.panels.find((p) => p.dataset.bundle === mode) || null;
      }

      onModeChange() {
        const mode = this.activeMode; // 'single' | 'a' | 'b' | null
        const isBundle = mode === 'a' || mode === 'b';
        this.panels.forEach((panel) => {
          panel.hidden = panel.dataset.bundle !== mode;
        });
        // Native qty + buy buttons are hidden only while a bundle is selected.
        if (this.productInfo) this.productInfo.classList.toggle('tailigent-bundle-active', isBundle);
        // Once a bundle is picked, reveal the single-ball option so the customer
        // can switch back to the regular purchase.
        if (isBundle && this.singleCard) this.singleCard.hidden = false;
        this.showError('');
        this.broadcast();
      }

      collectItems(panel, silent) {
        if (!panel) return null;

        const ids = [];

        // First ball always comes from the main product variant picker.
        const mainBallId = this.getMainBallId();
        if (!mainBallId) {
          if (!silent) this.showError('בחרו צבע לכדור למעלה.');
          return null;
        }
        ids.push(mainBallId);

        // Extra items chosen inside this panel (shell, second ball).
        const inputs = Array.from(panel.querySelectorAll('.tailigent-bundle__variant-input'));
        const groups = {};
        inputs.forEach((input) => {
          if (input.type === 'radio') {
            (groups[input.name] = groups[input.name] || []).push(input);
          } else {
            ids.push(input.value); // hidden single-variant input (e.g. shell)
          }
        });

        for (const name in groups) {
          const checked = groups[name].find((i) => i.checked);
          if (!checked) {
            if (!silent) this.showError('בחרו צבע לכל פריט.');
            return null;
          }
          ids.push(checked.value);
        }

        const counts = {};
        ids.forEach((id) => {
          counts[id] = (counts[id] || 0) + 1;
        });
        return Object.keys(counts).map((id) => ({ id: Number(id), quantity: counts[id] }));
      }

      /* Tell listeners (sticky footer) the current price + items. */
      broadcast() {
        const input = this.activeInput;
        let detail;
        if (input && input.value !== 'single') {
          const items = this.collectItems(this.activePanel(), true);
          detail = { mode: input.value, price: input.dataset.price || '', items: items || [] };
        } else {
          // Nothing selected, or the single-ball option -> native single purchase.
          const mainId = this.getMainBallId();
          detail = {
            mode: 'single',
            price: (input && input.dataset.price) || this.dataset.singlePrice || '',
            items: mainId ? [{ id: Number(mainId), quantity: 1 }] : [],
          };
        }
        document.dispatchEvent(new CustomEvent('tailigent:bundle-change', { detail }));
      }

      addBundle(buyNow, panel) {
        if (!panel || this.loading || this.redirecting) return;
        this.showError('');

        const items = this.collectItems(panel, false);
        if (!items) return;

        const body = { items };
        if (!buyNow && this.cart && typeof this.cart.getSectionsToRender === 'function') {
          body.sections = this.cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          if (typeof this.cart.setActiveElement === 'function') this.cart.setActiveElement(document.activeElement);
        }

        const button = panel.querySelector(buyNow ? '.tailigent-bundle__buy-now' : '.tailigent-bundle__atc');
        this.setLoading(true, button);

        const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';

        fetch(`${addUrl}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.showError(response.description || response.message || 'משהו השתבש. נסו שוב.');
              return;
            }

            if (buyNow) {
              this.redirecting = true;
              window.location.href = '/checkout';
              return;
            }

            // A multi-item add doesn't return a top-level line `key`/`id`, but the
            // theme's cart notification/drawer renderer needs one. Borrow the last
            // added item's so the repaint can find its block.
            if (!response.key && Array.isArray(response.items) && response.items.length) {
              const lastItem = response.items[response.items.length - 1];
              response.key = lastItem.key;
              response.id = lastItem.id;
            }

            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: items[0].id,
                cartData: response,
              });
            }

            // The items are already in the cart; never let a render hiccup surface
            // as a failure - fall back to the cart page instead.
            try {
              if (this.cart && typeof this.cart.renderContents === 'function') {
                if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
                this.cart.renderContents(response);
              } else {
                window.location = (window.routes && window.routes.cart_url) || '/cart';
              }
            } catch (renderError) {
              console.error('Bundle added; cart refresh failed, opening cart.', renderError);
              window.location = (window.routes && window.routes.cart_url) || '/cart';
            }
          })
          .catch((error) => {
            console.error(error);
            this.showError('משהו השתבש. נסו שוב.');
          })
          .finally(() => {
            if (!this.redirecting) this.setLoading(false, button);
          });
      }

      setLoading(loading, button) {
        this.loading = loading;
        this.querySelectorAll('.tailigent-bundle__action').forEach((el) => {
          if (loading) {
            el.setAttribute('aria-disabled', 'true');
          } else {
            el.removeAttribute('aria-disabled');
          }
        });
        if (!button) return;
        button.classList.toggle('loading', loading);
        const spinner = button.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !loading);
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.hidden = !message;
      }
    }
  );
}
